'use client';

import AlternativesDropdown from '@components/AlternativesDropdown/AlternativesDropdown';
import ProductAlternativeOverlay from '@components/AlternativesOverlay/ProductAlternativeOverlay';
import { Box, Flex, FormLabel, NumberStepInput, Typography } from '@coolblue-development/freddy';
import miniProductPageAddAccessoryToCartTrigger from '@coolblue-development/ga4-events/triggers/MiniProductPage/addAccessoryToCartTrigger';
import productTypeAddAccessoryToCartTrigger from '@coolblue-development/ga4-events/triggers/ProductTypeSection/addAccessoryToCartTrigger';
import changeQuantityTrigger from '@coolblue-development/ga4-events/triggers/ProductTypeSection/changeQuantityTrigger';
import productTypeSelectColorAlternativeTrigger from '@coolblue-development/ga4-events/triggers/ProductTypeSection/selectColorAlternativeTrigger';
import productTypeShowCollectionTrigger from '@coolblue-development/ga4-events/triggers/ProductTypeSection/showCollectionTrigger';
import automatedAddAccessoryToCartTrigger from '@coolblue-development/ga4-events/triggers/RecommendedAccessories/addAccessoryToCartTrigger';
import automatedSelectColorAlternativeTrigger from '@coolblue-development/ga4-events/triggers/RecommendedAccessories/selectColorAlternativeTrigger';
import automatedShowCollectionTrigger from '@coolblue-development/ga4-events/triggers/RecommendedAccessories/showCollectionTrigger';
import { TrackImmediate, useAnalytics } from '@coolblue-development/next-analytics/client';
import { ColorAlternative, ColorPicker } from '@coolblue-development/next-patterns/client';
import { AddToCartButton } from '@coolblue-development/next-shopping-cart/client';
import { useAccessoryProductCardContext } from '@features/add-to-cart-page/components/Sections/AccessoryProductCard/AccessoryProductCard.context';
import { HighlightedAlternative, Product } from '@features/add-to-cart-page/typings/Product';
import { USBCChargerIncluded } from '@features/shared/USBCChargerIncluded';
import useTestId from '@hooks/useTestId';
import { useTranslation } from '@i18n/useTranslation';
import { addProductToShoppingCart } from '@utils/nextShoppingCartActions/addProductToShoppingCart';
import { ForwardedRef, ReactElement, ReactNode, RefObject, useMemo } from 'react';
import { cloneElement, forwardRef } from 'react';
import React from 'react';

import { mapProductToSummaryItem, useSummaryStore } from '../../SummaryStore';
import { useCustomerContext } from '../CustomerContext/CustomerContext';

type Props = {
  description?: string;
  energyLabel?: ReactNode;
  productTitle: ReactNode;
  salesPrice: ReactNode;
  promotionalProductImage: ReactNode;
  hasAlternatives: boolean;
  product: Product;
  productTypeNameSingular?: string;
  productTypeNamePlural?: string;
  productTypeId: number;
  classificationId?: string;
  mainProductId: number;
  accessoryProductCardIndex: number;
  accessoryProductCardTotalIndices: number;
  highlightedAlternative?: HighlightedAlternative;
  isHighlighted?: boolean;
  isAutomated: boolean;
  colorAlternatives?: ColorAlternative[];
  onSelectColorAlternative?: (index: string) => void;
  isMiniProductPageOpen?: boolean;
};

const AccessoryProductCard = forwardRef(
  (
    {
      description,
      energyLabel,
      productTitle,
      salesPrice,
      promotionalProductImage,
      hasAlternatives,
      product,
      productTypeNameSingular,
      productTypeNamePlural,
      productTypeId,
      classificationId,
      mainProductId,
      accessoryProductCardIndex,
      accessoryProductCardTotalIndices,
      highlightedAlternative,
      isHighlighted = false,
      isAutomated,
      colorAlternatives = [],
      onSelectColorAlternative,
      isMiniProductPageOpen
    }: Props,
    ref: ForwardedRef<HTMLButtonElement | null>
  ) => {
    const { t } = useTranslation('webshop');
    const { trackEvent } = useAnalytics();
    const { addItem } = useSummaryStore();
    const context = useAccessoryProductCardContext();
    const { isBusinessCustomer } = useCustomerContext();
    const testId = useTestId('accessory-product-card-quantity');

    const showColorPicker = useMemo(
      () =>
        colorAlternatives.length > 1 &&
        colorAlternatives.findIndex(alternative => alternative.productId === product.id) > -1,
      [colorAlternatives, product]
    );

    const handleQuantityChange = (quantity: number) => {
      const prevQuantity = context.selectedProductQuantity || 1;

      context.setSelectedProductQuantity(quantity, product?.orderQuantityLimit);

      if (!isAutomated) {
        const trackingParams = {
          sub_feature: 'product_type_section' as const,
          item_id: product.id,
          type: prevQuantity < quantity ? ('add' as const) : ('remove' as const),
          quantity:
            !!product?.orderQuantityLimit && quantity > product?.orderQuantityLimit
              ? product?.orderQuantityLimit
              : quantity
        };

        trackEvent(changeQuantityTrigger(trackingParams));
      }
    };

    const quantitySelector = (
      <NumberStepInput
        value={context.selectedProductQuantity || 1}
        min={1}
        onChange={quantity => handleQuantityChange(quantity)}
        aria-label={t('addToCart.productQuantity', { productName: product.name })}
        max={product.orderQuantityLimit || undefined}
      />
    );

    function trackAddToCart() {
      const eventParams = {
        primary_item_id: mainProductId,
        attached_item_id: product.id,
        index: accessoryProductCardIndex,
        total_indices: accessoryProductCardTotalIndices,
        sub_feature: isAutomated ? 'recommended_accessories' : 'product_type_section',
        card_has_highlighted_segment: isHighlighted
      };

      if (isMiniProductPageOpen) {
        trackEvent(
          miniProductPageAddAccessoryToCartTrigger(
            eventParams as Parameters<typeof miniProductPageAddAccessoryToCartTrigger>[0]
          )
        );
        return;
      }

      trackEvent(
        isAutomated
          ? automatedAddAccessoryToCartTrigger({
              ...eventParams,
              sub_feature: 'recommended_accessories'
            })
          : productTypeAddAccessoryToCartTrigger({
              ...eventParams,
              sub_feature: 'product_type_section'
            })
      );
    }

    return (
      <Flex
        direction={{ xs: 'row', md: 'column' }}
        gap={{ xs: 400, md: 300 }}
        justifyContent='space-between'
        padding={{ xs: 400, md: 600 }}
        sx={theme => ({
          border: `1px solid ${theme.color.border.base}`,
          borderRadius: theme.borderRadius[100]
        })}
      >
        <Box sx={{ flex: '0 0 25%' }} hideFrom='md'>
          {cloneElement(promotionalProductImage as ReactElement, {
            size: 'small',
            labelsPosition: 'belowImage'
          })}
        </Box>
        <Box hideUntil='md'>
          {cloneElement(promotionalProductImage as ReactElement, {
            size: 'medium',
            labelsPosition: 'middle'
          })}
        </Box>
        <Flex direction='column' gap={300} justifyContent='space-between' flexGrow={1}>
          <Box>
            <Box>
              {cloneElement(productTitle as ReactElement, {
                size: { xs: 'small', md: 'medium' }
              })}
            </Box>
            {description && (
              <Box mt={200}>
                <Typography>{description}</Typography>
              </Box>
            )}
          </Box>
          <Box>
            {showColorPicker && (
              <Box mb={300}>
                <TrackImmediate
                  trigger={(isAutomated
                    ? automatedShowCollectionTrigger
                    : productTypeShowCollectionTrigger)({
                    sub_feature: 'color_collection'
                  })}
                />
                <ColorPicker
                  colorAlternatives={colorAlternatives}
                  onChange={value => {
                    const params = {
                      index: +value,
                      total_indices: colorAlternatives?.length,
                      item_id: colorAlternatives[+value].productId
                    };
                    trackEvent(
                      isAutomated
                        ? automatedSelectColorAlternativeTrigger(params)
                        : productTypeSelectColorAlternativeTrigger(params)
                    );

                    onSelectColorAlternative?.(value);
                  }}
                  value={
                    context.selectedColorAlternatives[context.initialProductId]?.toString() || '0'
                  }
                />
              </Box>
            )}
            {!showColorPicker &&
              hasAlternatives &&
              highlightedAlternative &&
              highlightedAlternative?.slots?.length > 1 &&
              isHighlighted && (
                <Box mb={300}>
                  <AlternativesDropdown
                    highlightedAlternative={highlightedAlternative}
                    product={product}
                    isAutomated={isAutomated}
                    accessoryProductCardIndex={accessoryProductCardIndex}
                    accessoryProductCardTotalIndices={accessoryProductCardTotalIndices}
                  />
                </Box>
              )}
            {product.hasUsbCChargerIncluded !== null && (
              <Box mb={200}>
                <USBCChargerIncluded hasUsbCChargerIncluded={product.hasUsbCChargerIncluded} />
              </Box>
            )}
            <Box mb={300}>
              {energyLabel && (
                <Box display={'contents'}>
                  {cloneElement(energyLabel as ReactElement, {
                    size: 'medium',
                    direction: 'right'
                  })}
                </Box>
              )}
            </Box>

            {isBusinessCustomer && (
              <Flex {...testId} mr={400} mb={300} direction='column'>
                <FormLabel mb={200}> {t('addToCart.quantity')}</FormLabel>
                {quantitySelector}
              </Flex>
            )}

            <Flex direction='row' gap={200} justifyContent='space-between' alignItems='center'>
              <Box>
                {cloneElement(salesPrice as ReactElement, {
                  size: 'medium',
                  labelsPosition: 'middle'
                })}
              </Box>
              <AddToCartButton
                action={async () => {
                  await addProductToShoppingCart({
                    productId: product.id,
                    params: { quantity: context.selectedProductQuantity || 1 }
                  });
                }}
                onSuccess={() => {
                  addItem(mapProductToSummaryItem(product, context.selectedProductQuantity));
                }}
                ariaLabel={t('addToCart.addToCartLabel')}
                variant='compact'
                ref={ref}
                onClick={trackAddToCart}
              />
            </Flex>
            {hasAlternatives && (
              <ProductAlternativeOverlay
                mainProductId={mainProductId}
                productTypeNameSingular={productTypeNameSingular}
                productTypeNamePlural={productTypeNamePlural}
                productTypeId={productTypeId}
                classificationId={classificationId}
                accessoryProductCardIndex={accessoryProductCardIndex}
                accessoryProductCardTotalIndices={accessoryProductCardTotalIndices}
                ctaRef={ref as RefObject<HTMLInputElement>}
                isAutomated={isAutomated}
                quantity={quantitySelector}
              />
            )}
          </Box>
        </Flex>
      </Flex>
    );
  }
);

AccessoryProductCard.displayName = 'AccessoryProductCard';

export default AccessoryProductCard;
